import { jsonrepair } from 'jsonrepair';
import { schemaToFields } from '../../utils/form-renderer';
import type { ChatOrchestrator } from '../../services/orchestrator';
import type { ChatMessage, FormFieldDef, McpToolDef } from '../../types';
import { store } from '../../state/store';
import { generateId } from '../inputBar/helpers';
import { LatestAttachmentContext } from '../../utils/types';
import { buildCandidateKeys, matchAttachmentRule, normalizeMatchText, parseFileReference } from '../../utils/file-field';

/**
 * Parse a ```form JSON block, tolerating the near-valid JSON the model occasionally
 * emits (e.g. a stray comma where a colon belongs, especially with more fields /
 * non-Latin labels in play). jsonrepair fixes most LLM JSON slips but throws its own
 * "Colon expected" error at an exact position rather than guessing on that one -
 * drive it by replacing/inserting a colon at that position and retrying.
 */
export function parseFormSpecJson(raw: string): Record<string, unknown> {
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    // fall through to the repair loop below
  }

  let text = raw;
  for (let attempt = 0; attempt < 10; attempt++) {
    try {
      return JSON.parse(jsonrepair(text)) as Record<string, unknown>;
    } catch (err) {
      const position = (err as { position?: number }).position;
      if (!/Colon expected/i.test((err as Error).message || '') || typeof position !== 'number') {
        throw err;
      }
      text = text[position] === ','
        ? text.slice(0, position) + ':' + text.slice(position + 1)
        : text.slice(0, position) + ':' + text.slice(position);
    }
  }
  // Last attempt - let a genuine failure propagate to the caller's catch.
  return JSON.parse(jsonrepair(text)) as Record<string, unknown>;
}

let moduleOrchestratorRef: ChatOrchestrator | null = null;
let orchestratorRecoveryHandler: (() => Promise<ChatOrchestrator | null>) | null = null;

export interface PromptSuggestionItem {
  text: string;
  requiredFields?: string[];
  labels?: Record<string, string>;
  toolName: string;
}

export interface ChipFormState {
  toolName: string;
  chipText: string;
  labels?: Record<string, string>;
  fields: FormFieldDef[];
}

export interface AiFormPortalEntry {
  id: string;
  containerEl: HTMLElement;
  toolName?: string;
  fields: FormFieldDef[];
  initialValues?: Record<string, unknown>;
}

export interface ContentModalState {
  type: 'chart' | 'table' | 'image';
  chartSpec?: string;
  tableHtml?: string;
  imageUrl?: string;
  imageAlt?: string;
}

export function setOrchestratorRef(orch: ChatOrchestrator | null): void {
  moduleOrchestratorRef = orch;
}

export function getOrchestratorRef(): ChatOrchestrator | null {
  return moduleOrchestratorRef;
}

export function setOrchestratorRecoveryHandler(
  handler: (() => Promise<ChatOrchestrator | null>) | null,
): void {
  orchestratorRecoveryHandler = handler;
}

export async function ensureOrchestratorRef(): Promise<ChatOrchestrator | null> {
  if (moduleOrchestratorRef) {
    return moduleOrchestratorRef;
  }

  if (!orchestratorRecoveryHandler) {
    return null;
  }

  try {
    return await orchestratorRecoveryHandler();
  } catch (error) {
    console.warn('[mcp-chat-widget] Failed to recover orchestrator:', error);
    return null;
  }
}

export function parseSuggestion(
  val: unknown,
  toolName: string,
): PromptSuggestionItem | null {
  if (typeof val === 'string') return { text: val, toolName };

  if (val && typeof val === 'object' && 'text' in val) {
    const obj = val as Record<string, unknown>;
    const text = obj.text as string;
    if (typeof text !== 'string') return null;

    const requiredFields = Array.isArray(obj.requiredFields)
      ? obj.requiredFields.filter((field): field is string => typeof field === 'string')
      : undefined;

    const labels =
      obj.labels && typeof obj.labels === 'object'
        ? (obj.labels as Record<string, string>)
        : undefined;

    return {
      text,
      requiredFields: requiredFields && requiredFields.length > 0 ? requiredFields : undefined,
      labels,
      toolName,
    };
  }

  return null;
}

function isSuggestionEnabled(value: unknown): boolean {
  return !(value && typeof value === 'object' && (value as Record<string, unknown>).enabled === false);
}

export function getPromptSuggestions(tool: McpToolDef): PromptSuggestionItem[] {
  const raw =
    tool.annotations?.promptSuggestion ??
    (tool.inputSchema?.annotations as Record<string, unknown> | undefined)?.promptSuggestion;

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .filter(isSuggestionEnabled)
      .map((value) => parseSuggestion(value, tool.name))
      .filter((suggestion): suggestion is PromptSuggestionItem => suggestion !== null );
  }

  if (!isSuggestionEnabled(raw)) return [];

  const single = parseSuggestion(raw, tool.name);
  return single ? [single] : [];
}

export function getAiFormPersistenceKey(
  el: HTMLElement,
  toolName?: string,
): string | null {
  const msgEl = el.closest('[data-message-id]') as HTMLElement | null;
  const msgId = msgEl?.dataset.messageId;
  if (!msgId) return null;
  return `${msgId}:${toolName || ''}`;
}

export function buildAiFormFields(
  spec: Record<string, unknown>,
  tools: McpToolDef[],
  el: HTMLElement,
  messages: ChatMessage[],
): { toolName?: string; fields: FormFieldDef[] } | null {
  const toolName = spec.tool as string | undefined;
  const fieldsData = spec.fields as Record<string, unknown> | undefined;
  const aiLabels = spec.labels as Record<string, string> | undefined;
  let fields: FormFieldDef[] | undefined;

  if (toolName) {
    const toolDef = tools.find((tool) => tool.name === toolName);
    if (toolDef?.inputSchema) {
      fields = schemaToFields(toolDef.inputSchema, fieldsData || {});
    }
  }

  if (!fields && fieldsData) {
    fields = Object.entries(fieldsData).map(([name, value]) => ({
      name,
      label: aiLabels?.[name] || name,
      type: 'text' as const,
      defaultValue: value as string | undefined,
    }));
  }

  if ((!fields || fields.length === 0) && aiLabels) {
    fields = Object.entries(aiLabels)
      .filter(([, label]) => label)
      .map(([name, label]) => ({
        name,
        label,
        type: 'text' as const,
        defaultValue: undefined as string | undefined,
      }));
  }

  if (aiLabels && fields) {
    for (const field of fields) {
      const label = aiLabels[field.name];
      if (typeof label === 'string' && label) field.label = label;
    }
  }
  if (fields && toolName) {
    const toolDef = tools.find(td => td.name === toolName);
    const properties = (toolDef?.inputSchema as Record<string, unknown> | undefined)?.properties as Record<string, Record<string, unknown>> | undefined;
    const attachment = getLatestAttachmentContext();
    const latestToolResult = getLatestToolResultObject();
    const messageContextValues = extractMessageContextValues(
      (el.closest('.message-content') as HTMLElement) || el,
      messages,
    );
    const explicitPrefillContext = { attachment, latestToolResult, messageContextValues };

    for (const field of fields) {
      const prop = properties?.[field.name];
      const explicitPrefill = resolveExplicitPrefillValue(prop, explicitPrefillContext);
      if (explicitPrefill !== undefined) {
        (field as any).defaultValue = explicitPrefill;
        continue;
      }

      const inferred = inferAttachmentDefaultValue(field, prop, attachment);
      if (inferred !== undefined) {
        (field as any).defaultValue = inferred;
        continue;
      }

      const fromLatestResult = inferLatestResultDefaultValue(field, prop, latestToolResult);
      if (fromLatestResult !== undefined) {
        (field as any).defaultValue = fromLatestResult;
        continue;
      }

      const fromMessageContext = inferMessageContextDefaultValue(field, prop, messageContextValues);
      if (fromMessageContext !== undefined) {
        (field as any).defaultValue = fromMessageContext;
      }
    }
  }
  if (!fields || fields.length === 0) return null;

  return { toolName, fields };
}

export function formatFormValues(values: Record<string, unknown>, fields?: FormFieldDef[]): string {
  return Object.entries(values)
    .filter(([, value]) => value !== '' && value !== undefined)
    .map(([name, value]) => {
      const field = fields?.find(f => f.name === name);
      const displayKey = field?.label || name;
      if (field?.type === 'checkbox') {
        return `${displayKey}: ${value ? 'Yes' : 'No'}`;
      }
      const option = field?.options?.find(o => o.value === String(value));
      const displayValue = option ? option.label : value;
      return `${displayKey}: ${displayValue}`;
    })
    .join(', ');
}

export function buildAiFormMessage(
  toolName: string | undefined,
  values: Record<string, unknown>,
  fields?: FormFieldDef[],
): string {
  const formatted = formatFormValues(values, fields);
  return toolName
    ? `Form submitted for ${toolName}: ${formatted}`
    : `Form submitted: ${formatted}`;
}

export function buildChipMessage(
  chipText: string,
  values: Record<string, unknown>,
  fields?: FormFieldDef[],
): string {
  const formatted = formatFormValues(values, fields);
  return formatted ? `${chipText}: ${formatted}` : chipText;
}

/** Plain-text equivalent of the chip form's field list, for tools with forms disabled. */
export function buildMissingFieldsPrompt(
  suggestion: PromptSuggestionItem,
  language?: string,
): string {
  const fieldLabels = (suggestion.requiredFields || []).map(
    (fieldName) => suggestion.labels?.[fieldName] || fieldName,
  );
  const list = fieldLabels.map((label) => `- ${label}`).join('\n');
  return language === 'he'
    ? `כדי להמשיך, אנא ספק את הנתונים הבאים:\n${list}`
    : `To continue, please provide the following:\n${list}`;
}

/**
 * Forms are disabled for this suggestion's tool - ask for its required fields as a
 * plain-text question instead of opening the chip form. Without this, a disabled-form
 * tool with required fields silently sent the bare suggestion text with no follow-up
 * ask at all (unlike the enabled path, which always collects the fields first).
 */
export function promptForMissingFieldsAsText(
  suggestion: PromptSuggestionItem,
  language?: string,
): void {
  store.addMessage({
    id: generateId(),
    role: 'user',
    content: suggestion.text,
    timestamp: Date.now(),
  });
  store.addMessage({
    id: generateId(),
    role: 'assistant',
    content: buildMissingFieldsPrompt(suggestion, language),
    timestamp: Date.now(),
    awaitingInput: true,
  });
}



function flattenObjectEntries(
  obj: Record<string, unknown>,
  parentPath = '',
): Array<{ key: string; path: string; value: unknown }> {
  const out: Array<{ key: string; path: string; value: unknown }> = [];

  for (const [key, value] of Object.entries(obj)) {
    const path = parentPath ? `${parentPath}.${key}` : key;
    out.push({ key, path, value });

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      out.push(...flattenObjectEntries(value as Record<string, unknown>, path));
    }
  }

  return out;
}

function getMessageSourceText(container: HTMLElement, messages: ChatMessage[]): string {
  const msgEl = container.closest('[data-message-id]') as HTMLElement | null;
  const msgId = msgEl?.dataset.messageId;
  const message = msgId ? messages.find((m) => m.id === msgId) : undefined;
  if (message && typeof message.content === 'string') return message.content;

  return container.querySelector('.message-text')?.textContent || '';
}

function extractMessageContextValues(container: HTMLElement, messages: ChatMessage[]): Map<string, string> {
  const values = new Map<string, string>();
  const text = getMessageSourceText(container, messages);
  if (!text.trim()) return values;

  const addValue = (key: string, value: string) => {
    const normalizedKey = normalizeMatchText(key);
    const trimmedValue = value.trim();
    if (!normalizedKey || !trimmedValue) return;
    values.set(normalizedKey, trimmedValue);
  };

  // [^\S\n] (horizontal whitespace only) is used around colons instead of \s
  // so a missing value doesn't let the match bleed across the line break into
  // the next line's "key: value" pair.
  const labeledPattern = /([^\n:()]+?)[^\S\n]*\(([A-Za-z0-9_]+)\)[^\S\n]*:[^\S\n]*([^\n]+)/g;
  for (const match of text.matchAll(labeledPattern)) {
    addValue(match[1], match[3]);
    addValue(match[2], match[3]);
  }

  // Strips leading list/blockquote decoration (-, *, •, >, or combinations like "> -")
  // so markdown like "> - Material Number: MAT-10042" yields key "Material Number".
  const simplePattern = /^[^\S\n]*(?:[>*\-•][^\S\n]*)*([^:\n]+?)[^\S\n]*:[^\S\n]*([^\n]+)$/gm;
  for (const match of text.matchAll(simplePattern)) {
    addValue(match[1], match[2]);
  }

  return values;
}

function getLatestAttachmentContext(): LatestAttachmentContext | null {
  const messages = store.getState().messages;

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'user') continue;

    const attachment = msg.attachments?.[msg.attachments.length - 1];
    if (attachment) {
      return {
        filename: attachment.filename,
        mimeType: attachment.mimeType,
        storagePath: attachment.storagePath,
        originalName: attachment.originalName,
        sizeBytes: attachment.size,
        fileId: attachment.fileId,
        fileHandle: attachment.fileHandle,
      };
    }

    const content = typeof msg.content === 'string' ? msg.content : '';
    const fileRefs = [...content.matchAll(/\[File:\s*([^\]]+?)\]/g)];
    if (fileRefs.length === 0) continue;

    const lastRef = fileRefs[fileRefs.length - 1][1];
    return parseFileReference(lastRef);
  }

  return null;
}

type ExplicitPrefillContext = {
  attachment: LatestAttachmentContext | null;
  latestToolResult: Record<string, unknown> | null;
  messageContextValues: Map<string, string>;
};

function getStringProp(obj: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = obj?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getExplicitPrefillSource(prop: Record<string, unknown> | undefined): string | undefined {
  return (
    getStringProp(prop, 'x-prefillFrom') ||
    getStringProp(prop, 'prefillFrom') ||
    getStringProp(prop, 'x-prefill-from')
  );
}

function getValueAtPath(source: unknown, pathSegments: string[]): unknown {
  let current: unknown = source;

  for (const segment of pathSegments) {
    if (!segment) continue;
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

function serializePrefillValue(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value) || typeof value === 'object') {
    return JSON.stringify(value);
  }
  return undefined;
}

function resolveExplicitPrefillValue(
  prop: Record<string, unknown> | undefined,
  contexts: ExplicitPrefillContext,
): string | undefined {
  const source = getExplicitPrefillSource(prop);
  if (!source) return undefined;

  const [root, ...pathSegments] = source.split('.');

  if (root === 'attachment') {
    if (!contexts.attachment) return undefined;
    const value = pathSegments.length > 0
      ? getValueAtPath(contexts.attachment as unknown, pathSegments)
      : contexts.attachment;
    return serializePrefillValue(value);
  }

  if (root === 'latestResult') {
    if (!contexts.latestToolResult) return undefined;
    const value = pathSegments.length > 0
      ? getValueAtPath(contexts.latestToolResult, pathSegments)
      : contexts.latestToolResult;
    return serializePrefillValue(value);
  }

  if (root === 'messageContext') {
    if (pathSegments.length === 0) return undefined;
    const key = pathSegments.join(' ');
    const candidates = buildCandidateKeys(key, key, undefined);
    for (const candidate of candidates) {
      const value = contexts.messageContextValues.get(normalizeMatchText(candidate));
      if (value !== undefined) return value;
    }
    return undefined;
  }

  return undefined;
}

function inferAttachmentDefaultValue(
  field: { name: string; label: string; defaultValue?: string | number | boolean },
  prop: Record<string, unknown> | undefined,
  attachment: LatestAttachmentContext | null,
): string | undefined {
  if (!attachment || field.defaultValue != null) return undefined;
  return matchAttachmentRule(field, prop, attachment);
}

function getLatestToolResultObject(): Record<string, unknown> | null {
  const toolCalls = store.getState().toolCalls;

  for (let i = toolCalls.length - 1; i >= 0; i--) {
    const raw = toolCalls[i].result;
    if (!raw) continue;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && typeof parsed[0]?.text === 'string') {
        const nested = JSON.parse(parsed[0].text);
        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
          return nested as Record<string, unknown>;
        }
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // ignore non-JSON results
    }
  }

  return null;
}

function inferLatestResultDefaultValue(
  field: { name: string; label: string; defaultValue?: string | number | boolean },
  prop: Record<string, unknown> | undefined,
  latestToolResult: Record<string, unknown> | null,
): string | undefined {
  if (field.defaultValue != null || !latestToolResult) return undefined;

  const candidates = buildCandidateKeys(field.name, field.label, prop);
  const flattened = flattenObjectEntries(latestToolResult);

  for (const entry of flattened) {
    const entryKey = normalizeMatchText(`${entry.path} ${entry.key}`);
    if (!candidates.some(candidate => candidate && (entryKey === candidate || entryKey.endsWith(` ${candidate}`) || entryKey.includes(candidate)))) {
      continue;
    }

    if (entry.value == null) return undefined;
    if (typeof entry.value === 'string' || typeof entry.value === 'number' || typeof entry.value === 'boolean') {
      return String(entry.value);
    }
    if (Array.isArray(entry.value) || typeof entry.value === 'object') {
      return JSON.stringify(entry.value);
    }
  }

  return undefined;
}

function inferMessageContextDefaultValue(
  field: { name: string; label: string; defaultValue?: string | number | boolean },
  prop: Record<string, unknown> | undefined,
  messageContextValues: Map<string, string>,
): string | undefined {
  if (field.defaultValue != null || messageContextValues.size === 0) return undefined;

  const candidates = buildCandidateKeys(field.name, field.label, prop);
  for (const candidate of candidates) {
    const value = messageContextValues.get(normalizeMatchText(candidate));
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
}

